import { getIdToken } from "firebase/auth";
import firebase from "./firebase";
import { SendMessageRequest, SendMessageResponse, StreamGiftsResponse, StreamMentionableUsersResponse } from "./models/stream-message";

interface ServerTimeResponse {
  timestamp: {
    _seconds: number;
    _nanoseconds: number;
  };
  milliseconds: number;
  seconds: number;
  nanoseconds: number;
}

// API Client 單例
export class APIClient {
  private serverUrl: string;

  constructor(serverUrl?: string) {
    this.serverUrl = serverUrl || import.meta.env.VITE_BACKEND_SERVER;
  }

  async apiRequest(
    endpoint: string,
    { optionalAuth = false, ...options }: any = {},
  ) {
    try {
      if (!this.serverUrl) {
        throw new Error("Server URL is not defined");
      }

      const currentUser = firebase.auth?.currentUser;
      if (!optionalAuth && !currentUser) return null;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      const token = currentUser ? await getIdToken(currentUser) : null;
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.serverUrl}${endpoint}`, {
        ...options,
        headers,
      });

      const acceptedStatuses = [200, 201, 206, 207];
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (acceptedStatuses.includes(response.status)) {
        return await response.json();
      }
      return await response.text();
    } catch (error) {
      console.error("API Request failed:", error);
      throw error;
    }
  }

  async getGuestVideos(start?: string) {
    return this.apiRequest(`/videos/guest?start=${start}`, {
      optionalAuth: true,
    });
  }

  async getVideosFeed(start?: string) {
    return this.apiRequest(`/feed/videos?start=${start}`);
  }

  async getCreator(creatorId: string) {
    const isSlug = creatorId.startsWith("@");
    const cleanId = isSlug ? creatorId.slice(1) : creatorId;
    const endpoint = isSlug
      ? `/creators/slug/${cleanId}`
      : `/creators/${cleanId}`;
    return this.apiRequest(endpoint, { optionalAuth: true });
  }

  async followCreator(creatorId: string) {
    return this.apiRequest(`/followed-creators/${creatorId}`, {
      method: "POST",
    });
  }

  async unfollowCreator(creatorId: string) {
    return this.apiRequest(`/followed-creators/${creatorId}`, {
      method: "DELETE",
    });
  }

  async getExplore() {
    return this.apiRequest(`/explore`, { optionalAuth: true });
  }

  async getGifts(): Promise<
    {
      id: string;
      group: string;
      price: number;
      name?: string;
      path?: string;
      effect?: string[];
      animation?: {
        path: string;
        duration?: number;
        loop?: boolean;
      };
    }[]
  > {
    return this.apiRequest(`/gifts`, { optionalAuth: true });
  }

  async getCreatorVideos(creatorId: string, next?: string | null) {
    return this.apiRequest(`/creators/${creatorId}/videos?next=${next || ""}`, {
      optionalAuth: true,
    });
  }

  // api.getExploreSettingsByChunk = useCallback(
  //   async (id, offset = 0, limit = 5) =>
  //     apiRequest(`/explore/chunks/${id}?offset=${offset}&limit=${limit}`),
  //   [apiRequest]
  // );

  async getExploreSettingsByChunk(id: string, offset = 0, limit = 5) {
    return this.apiRequest(
      `/explore/chunks/${id}?offset=${offset}&limit=${limit}`,
      {
        optionalAuth: true,
      },
    );
  }

  async getAllCreators() {
    return this.apiRequest(`/creators`, { optionalAuth: true });
  }

  async getLiveStreams(limit = 50, next?: string) {
    const params = new URLSearchParams();
    params.set('limit', limit.toString());
    if (next) {
      params.set('next', next);
    }
    return this.apiRequest(`/streams/live?${params.toString()}`, { optionalAuth: true });
  }

  async getLiveStream(streamId: string) {
    return this.apiRequest(`/streams/${streamId}`, { optionalAuth: true });
  }

  async getStreamSourceUrl(streamId: string) {
    const response = await this.apiRequest(`/streams/${streamId}/source`, { optionalAuth: true });
    // console.log(`[APIClient] Stream source URL:`, response);
    return response;    
  }

  async getStreamGifts(): Promise<StreamGiftsResponse> {
    return this.apiRequest('/streams/gifts', { optionalAuth: true });
  }

  async sendStreamMessage(streamId: string, payload: SendMessageRequest): Promise<SendMessageResponse> {
    return this.apiRequest(`/streams/${streamId}/chat`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async getStreamMentionableUsers(streamId: string): Promise<StreamMentionableUsersResponse> {
    return this.apiRequest(`/streams/${streamId}/mentionable-users`, { optionalAuth: true });
  }

  async checkStreamPayment(streamId: string) {    
    try {
      const result = await this.apiRequest(`/streams/${streamId}/pay`);
      if (!result.endTimestamp) {
        return 0;
      }
      return result.endTimestamp._seconds;
    } catch (error) {      
      // If it's a 401/403, return a default "no payment" response
      if (error instanceof Error && (error.message?.includes('401') || error.message?.includes('403'))) {
        console.log(`[APIClient] Authentication error, returning no payment response`);
        return 0;
      }
      
      throw error;
    }
  }

  async purchaseStreamTicket(streamId: string) {    
    const result = await this.apiRequest(`/streams/${streamId}/pay`, {
      method: 'POST',
      body: JSON.stringify({})
    });
    console.log(`[APIClient] ${streamId} - Purchase ticket result:`, {
      result,
      endTimestamp: result.endTimestamp,
      endTimestampSeconds: result.endTimestamp ? result.endTimestamp._seconds : null
    });
    return result.endTimestamp._seconds;
  }

  async getStreamLeaderboard(creatorId: string, params: {
    startDate?: number;
    endDate?: number;
    limit?: number;
    nextCursor?: string;
  } = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.startDate) {
      queryParams.set('startDate', params.startDate.toString());
    }
    if (params.endDate) {
      queryParams.set('endDate', params.endDate.toString());
    }
    if (params.limit) {
      queryParams.set('limit', params.limit.toString());
    }
    if (params.nextCursor) {
      queryParams.set('nextCursor', params.nextCursor);
    }
    
    const endpoint = `/stream-leaderboard/${creatorId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.apiRequest(endpoint, { optionalAuth: true });
  }

  async getServerTime() {
    try {
      const result = await this.apiRequest('/time', { optionalAuth: true });
      return result;
    } catch (error) {
      console.error('[APIClient] Failed to get server time:', error);
      throw error;
    }
  }

  async banStreamUser(creatorId: string, userId: string, reason: string, expiresAt?: number | null) {
    return this.apiRequest(`/banned-stream-user/${creatorId}`, {
      method: 'POST',
      body: JSON.stringify({
        userId,
        reason,
        expiresAt
      })
    });
  }

  async globalBanStreamUser(userId: string, reason: string, expiresAt?: number | null) {
    return this.apiRequest('/banned-stream-user', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        reason,
        expiresAt
      })
    });
  }

  async postStreamChatPresence(streamId: string) {
    return this.apiRequest(`/streams/${streamId}/chat-presence`, {
      method: 'POST',
      body: JSON.stringify({})
    });
  }

  async getJulyGoddessTopUsers() {
    return this.apiRequest('/july-goddess-campaign/top-users', { optionalAuth: true });
  }

  async getJulyGoddessTopCreators() {
    return this.apiRequest('/july-goddess-campaign/top-creators', { optionalAuth: true });
  }

}

// 建立 Singleton 實例
const apiClient = new APIClient();

export default apiClient;
